import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { useAuth } from '../lib/auth';
import { COMPETENCIES, SETTINGS, FACULTY_LIST, SUPERVISION_LEVELS, MILESTONE_LEVELS, REFLECTION_PROMPTS, APPROVAL_STATUSES } from '../lib/constants';

const empty = {
  date: new Date().toISOString().split('T')[0],
  mrn: '', setting: '', competency_ids: [], supervision: '',
  milestone_level: 3, case_summary: '', reflection: '',
  is_difficult: false, assigned_faculty_names: [],
};

export default function EntryForm({ existingEntry, onSaved, onCancel, isMobile }) {
  const { user, profile } = useAuth();
  const [form, setForm]     = useState(existingEntry ? { ...existingEntry, assigned_faculty_names: existingEntry.assigned_faculty_names || [] } : { ...empty });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [step, setStep]     = useState(1); // mobile uses steps
  const isLocked = existingEntry && existingEntry.approval_status !== 'pending';

  useEffect(() => {
    if (existingEntry) setForm({ ...existingEntry, assigned_faculty_names: existingEntry.assigned_faculty_names || [] });
    else setForm({ ...empty });
    setStep(1);
  }, [existingEntry]);

  const toggleComp = (id) => {
    if (isLocked) return;
    setForm(f => ({ ...f, competency_ids: f.competency_ids.includes(id) ? f.competency_ids.filter(x => x !== id) : [...f.competency_ids, id] }));
  };

  const toggleFaculty = (name) => {
    if (isLocked) return;
    setForm(f => ({ ...f, assigned_faculty_names: f.assigned_faculty_names.includes(name) ? f.assigned_faculty_names.filter(x => x !== name) : [...f.assigned_faculty_names, name] }));
  };

  const submit = async () => {
    if (!form.mrn.trim())                    { setError('MRN is required.'); return; }
    if (!form.case_summary.trim())           { setError('Please enter a case summary.'); return; }
    if (!form.competency_ids.length)         { setError('Select at least one competency.'); return; }
    if (!form.setting)                       { setError('Please select a clinical setting.'); return; }
    if (!form.assigned_faculty_names.length) { setError('Please select at least one faculty member.'); return; }
    setLoading(true); setError('');
    const payload = { ...form, user_id: user.id, fellow_name: profile?.full_name, fellow_email: user.email, approval_status: 'pending' };
    const result = existingEntry?.id
      ? await supabase.from('entries').update(payload).eq('id', existingEntry.id)
      : await supabase.from('entries').insert(payload);
    if (result.error) { setError(result.error.message); setLoading(false); return; }
    setLoading(false); onSaved();
  };

  const statusInfo = existingEntry ? APPROVAL_STATUSES[existingEntry.approval_status] : null;

  // ── MOBILE STEP VIEW ──────────────────────────────────────────
  if (isMobile) {
    const totalSteps = 4;
    return (
      <div style={{ minHeight: '100vh', background: '#F7F4F0', paddingBottom: 80 }}>
        {/* Header */}
        <div style={{ background: '#fff', padding: '14px 16px', borderBottom: '1px solid #E8E4E0', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#2D6A8A', fontSize: 14, cursor: 'pointer', padding: 0 }}>← Back</button>
            <span style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F' }}>
              {existingEntry ? 'Edit Case' : 'Log New Case'}
            </span>
            <span style={{ fontSize: 12, color: '#999' }}>Step {step}/{totalSteps}</span>
          </div>
          {/* Progress bar */}
          <div style={{ height: 3, background: '#E8E4E0', borderRadius: 10, marginTop: 10 }}>
            <div style={{ height: '100%', background: '#1A3A5C', borderRadius: 10, width: `${(step / totalSteps) * 100}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>

        {statusInfo && (
          <div style={{ margin: 16, padding: '10px 14px', borderRadius: 8, background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.border}`, fontSize: 13, fontWeight: 600 }}>
            {statusInfo.label}
          </div>
        )}

        {isLocked && (
          <div style={{ margin: 16, padding: '12px 14px', borderRadius: 8, background: '#FFF8EE', border: '1px solid #F4DFA8', color: '#8A6B2A', fontSize: 13 }}>
            🔒 This entry has been reviewed and cannot be edited.
            {existingEntry.faculty_comment && <div style={{ marginTop: 6, fontStyle: 'italic' }}>"{existingEntry.faculty_comment}"</div>}
          </div>
        )}

        {error && <div style={{ margin: '0 16px 16px', padding: '10px 14px', borderRadius: 8, background: '#FFF0F0', border: '1px solid #F4A8A8', color: '#8A2D2D', fontSize: 13 }}>{error}</div>}

        <div style={{ padding: '16px 16px 0' }}>

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div>
              <div style={ms.stepTitle}>Basic Information</div>
              <MField label="Date">
                <input type="date" value={form.date} disabled={isLocked}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={ms.input} />
              </MField>
              <MField label="MRN — Medical Record Number *">
                <input value={form.mrn} disabled={isLocked} placeholder="e.g. 123456"
                  onChange={e => setForm(f => ({ ...f, mrn: e.target.value }))} style={ms.input} />
              </MField>
              <MField label="Clinical Setting *">
                <select value={form.setting} disabled={isLocked}
                  onChange={e => setForm(f => ({ ...f, setting: e.target.value }))} style={ms.input}>
                  <option value=''>— Select setting —</option>
                  {SETTINGS.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </MField>
              <MField label="Case Summary *">
                <textarea value={form.case_summary} rows={4} disabled={isLocked}
                  placeholder="Brief clinical context — no patient identifiers…"
                  onChange={e => setForm(f => ({ ...f, case_summary: e.target.value }))} style={ms.textarea} />
              </MField>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <input type="checkbox" id="diff" checked={form.is_difficult} disabled={isLocked}
                  onChange={e => !isLocked && setForm(f => ({ ...f, is_difficult: e.target.checked }))}
                  style={{ width: 20, height: 20 }} />
                <label htmlFor="diff" style={{ fontSize: 14, color: '#555' }}>⚑ Flag as Difficult Case</label>
              </div>
            </div>
          )}

          {/* STEP 2: Faculty */}
          {step === 2 && (
            <div>
              <div style={ms.stepTitle}>Supervising Faculty *</div>
              <p style={ms.stepSub}>Select all faculty who supervised this case</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {FACULTY_LIST.map(name => {
                  const checked = form.assigned_faculty_names.includes(name);
                  return (
                    <div key={name} onClick={() => !isLocked && toggleFaculty(name)}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, border: `1.5px solid ${checked ? '#2D6A8A' : '#E0DDD8'}`, background: checked ? '#EEF5FB' : '#fff', cursor: isLocked ? 'default' : 'pointer' }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, border: `2px solid ${checked ? '#2D6A8A' : '#C8C4C0'}`, background: checked ? '#2D6A8A' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {checked && <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 15, fontWeight: checked ? 600 : 400, color: checked ? '#2D6A8A' : '#333' }}>{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 3: Competencies + Level */}
          {step === 3 && (
            <div>
              <div style={ms.stepTitle}>Competencies *</div>
              <p style={ms.stepSub}>Select all that were addressed</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
                {COMPETENCIES.map(c => (
                  <button key={c.id} onClick={() => toggleComp(c.id)} disabled={isLocked}
                    style={{ padding: '8px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, cursor: isLocked ? 'default' : 'pointer', fontFamily: "'DM Sans'", background: form.competency_ids.includes(c.id) ? c.color : 'transparent', color: form.competency_ids.includes(c.id) ? '#fff' : c.color, border: `1.5px solid ${c.color}` }}>
                    {c.label}
                  </button>
                ))}
              </div>

              <div style={ms.stepTitle}>Milestone Level</div>
              <p style={ms.stepSub}>Self-assessment — how did you perform?</p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {MILESTONE_LEVELS.map(l => (
                  <button key={l.n} disabled={isLocked}
                    onClick={() => !isLocked && setForm(f => ({ ...f, milestone_level: l.n }))}
                    style={{ flex: 1, padding: '14px 0', borderRadius: 10, cursor: isLocked ? 'default' : 'pointer', textAlign: 'center', fontFamily: "'DM Sans'", border: form.milestone_level === l.n ? '2px solid #1A3A5C' : '1.5px solid #E0DDD8', background: form.milestone_level === l.n ? '#1A3A5C' : '#fff', color: form.milestone_level === l.n ? '#F5F0E8' : '#555' }}>
                    <div style={{ fontFamily: "'DM Serif Display'", fontSize: 22 }}>{l.n}</div>
                    <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', opacity: 0.7, marginTop: 2 }}>{l.label}</div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic', textAlign: 'center', marginBottom: 16 }}>
                {MILESTONE_LEVELS.find(l => l.n === form.milestone_level)?.desc}
              </div>

              <MField label="Level of Supervision">
                <select value={form.supervision} disabled={isLocked}
                  onChange={e => setForm(f => ({ ...f, supervision: e.target.value }))} style={ms.input}>
                  <option value=''>— Select —</option>
                  {SUPERVISION_LEVELS.map(sv => <option key={sv} value={sv}>{sv}</option>)}
                </select>
              </MField>
            </div>
          )}

          {/* STEP 4: Reflection */}
          {step === 4 && (
            <div>
              <div style={ms.stepTitle}>Reflection <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}>(optional)</span></div>
              <p style={ms.stepSub}>A moment to reflect on this case</p>
              {!isLocked && (
                <select onChange={e => { if (e.target.value) setForm(f => ({ ...f, reflection: e.target.value })); }}
                  style={{ ...ms.input, marginBottom: 10 }} defaultValue="">
                  <option value=''>— Choose a reflection prompt —</option>
                  {REFLECTION_PROMPTS.map((r, i) => <option key={i} value={r}>{r}</option>)}
                </select>
              )}
              <textarea value={form.reflection} rows={5} disabled={isLocked}
                placeholder="Your reflection on this case…"
                onChange={e => setForm(f => ({ ...f, reflection: e.target.value }))} style={ms.textarea} />
            </div>
          )}
        </div>

        {/* Bottom navigation */}
        {!isLocked && (
          <div style={{ position: 'fixed', bottom: 70, left: 0, right: 0, padding: '12px 16px', background: '#fff', borderTop: '1px solid #E8E4E0', display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: '13px', background: '#EEE', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>← Back</button>
            )}
            {step < totalSteps ? (
              <button onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: '13px', background: '#1A3A5C', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Next →</button>
            ) : (
              <button onClick={submit} disabled={loading} style={{ flex: 2, padding: '13px', background: '#1E5C40', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Submitting…' : '✓ Submit for Review'}
              </button>
            )}
          </div>
        )}

        {isLocked && (
          <div style={{ padding: '0 16px', marginTop: 16 }}>
            <button onClick={onCancel} style={{ width: '100%', padding: '13px', background: '#EEE', border: 'none', borderRadius: 10, fontSize: 15, cursor: 'pointer' }}>← Back to Case Log</button>
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP VIEW ──────────────────────────────────────────────
  return (
    <div style={s.content}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>{existingEntry ? 'Case Entry' : 'Log New Case'}</h1>
          <p style={s.sub}>{isLocked ? 'This case has been reviewed by faculty' : 'Record your clinical encounter'}</p>
        </div>
        {statusInfo && <div style={{ ...s.statusBadge, background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.border}` }}>{statusInfo.label}</div>}
      </div>

      {isLocked && (
        <div style={s.lockedBanner}>
          🔒 This entry has been reviewed and can no longer be edited.
          {existingEntry.faculty_comment && <div style={{ marginTop: 8, fontStyle: 'italic' }}>Faculty comment: "{existingEntry.faculty_comment}"</div>}
        </div>
      )}

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={s.grid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Case Details</h3>
            <Field label="Date"><input type="date" value={form.date} disabled={isLocked} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={{ ...s.input, ...(isLocked ? s.dis : {}) }} /></Field>
            <Field label="MRN *"><input value={form.mrn} disabled={isLocked} placeholder="e.g. 123456" onChange={e => setForm(f => ({ ...f, mrn: e.target.value }))} style={{ ...s.input, ...(isLocked ? s.dis : {}) }} /></Field>
            <Field label="Clinical Setting *">
              <select value={form.setting} disabled={isLocked} onChange={e => setForm(f => ({ ...f, setting: e.target.value }))} style={{ ...s.input, ...(isLocked ? s.dis : {}) }}>
                <option value=''>— Select setting —</option>
                {SETTINGS.map(st => <option key={st} value={st}>{st}</option>)}
              </select>
            </Field>
            <Field label="Case Summary *"><textarea value={form.case_summary} rows={4} disabled={isLocked} placeholder="Brief clinical context — no patient identifiers…" onChange={e => setForm(f => ({ ...f, case_summary: e.target.value }))} style={{ ...s.textarea, ...(isLocked ? s.dis : {}) }} /></Field>
            <Field label="Reflection">
              {!isLocked && <select onChange={e => { if (e.target.value) setForm(f => ({ ...f, reflection: e.target.value })); }} style={{ ...s.input, marginBottom: 8 }} defaultValue=""><option value=''>— Choose a prompt —</option>{REFLECTION_PROMPTS.map((r, i) => <option key={i} value={r}>{r}</option>)}</select>}
              <textarea value={form.reflection} rows={3} disabled={isLocked} placeholder="Your reflection…" onChange={e => setForm(f => ({ ...f, reflection: e.target.value }))} style={{ ...s.textarea, ...(isLocked ? s.dis : {}) }} />
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="diff" checked={form.is_difficult} disabled={isLocked} onChange={e => !isLocked && setForm(f => ({ ...f, is_difficult: e.target.checked }))} style={{ width: 16, height: 16 }} />
              <label htmlFor="diff" style={{ fontSize: 13, color: '#555' }}>⚑ Flag as <strong>Difficult Case</strong></label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Supervising Faculty *</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {FACULTY_LIST.map(name => {
                const checked = form.assigned_faculty_names.includes(name);
                return (
                  <div key={name} onClick={() => !isLocked && toggleFaculty(name)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${checked ? '#2D6A8A' : '#E8E4E0'}`, background: checked ? '#EEF5FB' : '#FAFAF8', cursor: isLocked ? 'default' : 'pointer' }}>
                    <div style={{ width: 20, height: 20, borderRadius: 4, flexShrink: 0, border: `2px solid ${checked ? '#2D6A8A' : '#C8C4C0'}`, background: checked ? '#2D6A8A' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {checked && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? '#2D6A8A' : '#444' }}>{name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={s.card}>
            <h3 style={s.cardTitle}>Competencies *</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {COMPETENCIES.map(c => (
                <button key={c.id} onClick={() => toggleComp(c.id)} disabled={isLocked}
                  style={{ padding: '6px 13px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: isLocked ? 'default' : 'pointer', fontFamily: "'DM Sans'", background: form.competency_ids.includes(c.id) ? c.color : 'transparent', color: form.competency_ids.includes(c.id) ? '#fff' : c.color, border: `1.5px solid ${c.color}` }}>{c.label}</button>
              ))}
            </div>
          </div>

          <div style={s.card}>
            <h3 style={s.cardTitle}>Milestone Level</h3>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {MILESTONE_LEVELS.map(l => (
                <button key={l.n} disabled={isLocked} onClick={() => !isLocked && setForm(f => ({ ...f, milestone_level: l.n }))}
                  style={{ flex: 1, padding: '12px 0', borderRadius: 8, cursor: isLocked ? 'default' : 'pointer', textAlign: 'center', fontFamily: "'DM Sans'", border: form.milestone_level === l.n ? '2px solid #1A3A5C' : '1.5px solid #E8E4E0', background: form.milestone_level === l.n ? '#1A3A5C' : '#FAFAF8', color: form.milestone_level === l.n ? '#F5F0E8' : '#555' }}>
                  <div style={{ fontFamily: "'DM Serif Display'", fontSize: 20 }}>{l.n}</div>
                  <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', opacity: 0.7, marginTop: 2 }}>{l.label}</div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#888', fontStyle: 'italic', textAlign: 'center' }}>{MILESTONE_LEVELS.find(l => l.n === form.milestone_level)?.desc}</div>
          </div>

          <Field label="Level of Supervision">
            <select value={form.supervision} disabled={isLocked} onChange={e => setForm(f => ({ ...f, supervision: e.target.value }))} style={{ ...s.input, ...(isLocked ? s.dis : {}) }}>
              <option value=''>— Select —</option>
              {SUPERVISION_LEVELS.map(sv => <option key={sv} value={sv}>{sv}</option>)}
            </select>
          </Field>

          {!isLocked
            ? <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={onCancel} style={s.secBtn}>Cancel</button>
                <button onClick={submit} disabled={loading} style={{ ...s.priBtn, flex: 1, opacity: loading ? 0.7 : 1 }}>{loading ? 'Submitting…' : existingEntry ? 'Save Changes' : 'Submit for Faculty Review'}</button>
              </div>
            : <button onClick={onCancel} style={s.secBtn}>← Back to Case Log</button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 6, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
}

function MField({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
}

const ms = {
  stepTitle: { fontFamily: "'DM Serif Display'", fontSize: 20, color: '#1A1A1F', marginBottom: 4 },
  stepSub: { fontSize: 13, color: '#888', marginBottom: 16 },
  input: { width: '100%', padding: '13px 14px', border: '1.5px solid #E0DDD8', borderRadius: 10, fontSize: 15, color: '#1A1A1F', background: '#fff', outline: 'none', fontFamily: "'DM Sans'", boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '13px 14px', border: '1.5px solid #E0DDD8', borderRadius: 10, fontSize: 15, color: '#1A1A1F', background: '#fff', resize: 'vertical', outline: 'none', fontFamily: "'DM Sans'", lineHeight: 1.6, boxSizing: 'border-box' },
};

const s = {
  content: { padding: '36px 40px', maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontFamily: "'DM Serif Display'", fontSize: 30, color: '#1A1A1F', marginBottom: 4 },
  sub: { fontSize: 13, color: '#888' },
  statusBadge: { padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  lockedBanner: { background: '#FFF8EE', border: '1px solid #F4DFA8', color: '#8A6B2A', padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 20 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' },
  card: { background: '#fff', borderRadius: 12, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  cardTitle: { fontFamily: "'DM Serif Display'", fontSize: 17, color: '#1A1A1F', marginBottom: 4 },
  input: { width: '100%', padding: '10px 13px', border: '1.5px solid #E8E4E0', borderRadius: 8, fontSize: 13, color: '#1A1A1F', background: '#FAFAF8', outline: 'none', fontFamily: "'DM Sans'", boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 13px', border: '1.5px solid #E8E4E0', borderRadius: 8, fontSize: 13, color: '#1A1A1F', background: '#FAFAF8', resize: 'vertical', outline: 'none', fontFamily: "'DM Sans'", lineHeight: 1.6, boxSizing: 'border-box' },
  dis: { opacity: 0.65, cursor: 'default', background: '#F5F5F5' },
  priBtn: { background: '#1A3A5C', color: '#F5F0E8', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'" },
  secBtn: { background: '#EEE', color: '#444', border: 'none', borderRadius: 8, padding: '11px 18px', fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans'" },
  errorBox: { background: '#FFF0F0', border: '1px solid #F4A8A8', color: '#8A2D2D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 20 },
};
